import { Component, DestroyRef, inject, OnInit, QueryList, signal, ViewChildren } from '@angular/core';
import { FormsModule, NgForm } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { paymentMethodTypes } from 'tilby-models';
import type { NonFiscalDocuments, PaymentMethods, Printers } from 'tilby-models';

import { restManager } from 'app/ajs-upgraded-providers';

import { ConfigurationManagerService, EntityManagerService, PrinterDocumentType } from 'src/app/core';
import { isCashdrawerMethod } from 'src/app/core/constants/payment-method-types';
import { AlertDialogService, ConfirmDialogService } from 'src/app/dialogs';
import { AddEditPaymentConditionDialogService } from 'src/app/dialogs/add-edit-payment-condition-dialog/add-edit-payment-condition-dialog.component';
import { DocumentPrintersManagerDialogStateService } from 'src/app/dialogs/document-printers-manager-dialog/document-printers-manager-dialog.state.service';
import { color_items } from 'src/app/features/items/utils/item.constants';
import { EnvironmentConfig } from 'src/environments/environment-config';
import { ActionBtnComponent } from 'src/app/shared/components/master-detail-layout/action-btn/action-btn.component';

import { SettingsHubStateService } from '../../services/settings-hub-state.service';
import { PAYMENT_CONFIGURATION_FIELDS } from '../../registry/payment-configuration-fields';
import { LETTERS_NUMBERS_SPACES_PATTERN } from '../../registry/settings-patterns';

type AvailableDocument = { document_type: PrinterDocumentType; document_template?: NonFiscalDocuments; printer: Printers };
type DocumentPrinter = Partial<Printers> & { availableDocuments?: AvailableDocument[] };

type PaymentMethodItem = {
    method: PaymentMethods;
    isNew: boolean;
    expanded: boolean;
    overridePrinter: DocumentPrinter | null;
    overrideDocument: AvailableDocument | null;
};

type PaymentConditionOption = { value: number | null; name: string };

const MAX_PAYMENT_METHODS = 20;

/** Editor of the payment methods of the shop. Each payment method is saved on its own, as soon as the user confirms it. */
@Component({
    selector: 'app-settings-payment-methods',
    standalone: true,
    imports: [
        ActionBtnComponent,
        FormsModule,
        MatButtonModule,
        MatDividerModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatSelectModule,
        MatSlideToggleModule,
        TranslateModule
    ],
    templateUrl: './settings-payment-methods.component.html'
})
export class SettingsPaymentMethodsComponent implements OnInit {
    private readonly addEditPaymentConditionDialogService = inject(AddEditPaymentConditionDialogService);
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);
    private readonly confirmDialogService = inject(ConfirmDialogService);
    private readonly documentPrintersManagerDialogStateService = inject(DocumentPrintersManagerDialogStateService);
    private readonly entityManagerService = inject(EntityManagerService);
    private readonly restManagerService = inject(restManager);
    private readonly settingsState = inject(SettingsHubStateService);
    private readonly translateService = inject(TranslateService);

    @ViewChildren(NgForm) private readonly forms!: QueryList<NgForm>;

    protected readonly items = signal<PaymentMethodItem[]>([]);
    protected readonly paymentConditions = signal<PaymentConditionOption[]>([]);

    /** The yes/no options of a payment method, in the order they are shown */
    protected readonly toggles: { name: 'unclaimed' | 'hidden' | 'require_signature' | 'print_receipt' | 'print_signature_copy' | 'disable_open_cash_drawer'; label: string }[] = [
        { name: 'unclaimed', label: 'SETTINGS.SETTINGS.UNCLAIMED' },
        { name: 'hidden', label: 'SETTINGS.SETTINGS.HIDE_IN_CASHREGISTER' },
        { name: 'require_signature', label: 'SETTINGS.SETTINGS.REQUIRE_SIGNATURE' },
        { name: 'print_receipt', label: 'SETTINGS.SETTINGS.PRINT_RECEIPT' },
        { name: 'print_signature_copy', label: 'SETTINGS.SETTINGS.PRINT_SIGNATURE_COPY' },
        { name: 'disable_open_cash_drawer', label: 'SETTINGS.SETTINGS.DISABLE_OPEN_CASH_DRAWER' }
    ];

    protected readonly namePattern = LETTERS_NUMBERS_SPACES_PATTERN.source;
    protected readonly maxPaymentMethods = MAX_PAYMENT_METHODS;
    protected readonly types = paymentMethodTypes.map(type => ({ ...type })).sort((typeA, typeB) => typeA.name.localeCompare(typeB.name));
    protected readonly colors = [{ value: null as string | null, name: 'ITEMS.DETAILS.COLOR_NONE' }, ...color_items];
    protected readonly canResetConfiguration = EnvironmentConfig.environment !== 'stable';

    protected documentPrinters: DocumentPrinter[] = [];
    protected isMagoEnabled = false;

    constructor() {
        // The page asks before the user goes away from the payment methods that are being edited, as it does when one is closed
        const unregister = this.settingsState.registerPaymentMethodChangesCheck(() => !!this.forms?.some(form => !!form.dirty));

        inject(DestroyRef).onDestroy(unregister);
    }

    async ngOnInit() {
        const context = this.settingsState.context();

        this.isMagoEnabled = !!(context.getPreference('integrations.mago.publish_sales') || context.getPreference('integrations.mago.publish_customers'));

        this.documentPrinters = structuredClone(context.data.printers.filter(printer => ['fiscal', 'rt', 'receipt'].includes(printer.type)));
        this.documentPrintersManagerDialogStateService.scanPrintersCapabilities(this.documentPrinters as Printers[], context.data.nonFiscalDocuments, { skipProtocolCheck: true });
        this.documentPrinters.unshift({ id: undefined, name: this.translateService.instant('SETTINGS.SETTINGS.OVERRIDE_PRINTER_NONE'), type: 'wildcard' });

        await Promise.all([this.loadPaymentMethods(), this.loadPaymentConditions()]);
    }

    protected get canAddPaymentMethod() {
        return this.items().length < MAX_PAYMENT_METHODS;
    }

    protected configurationFields(item: PaymentMethodItem) {
        return PAYMENT_CONFIGURATION_FIELDS[item.method.payment_method_type_id] ?? [];
    }

    /** The reset is useful only for the payment methods that have a configuration to lose */
    protected hasConfiguration(item: PaymentMethodItem) {
        return Object.keys(item.method.configuration ?? {}).length > 0;
    }

    /** The fields of the configuration are edited in the object of the configuration, that a payment method may not have yet */
    protected onTypeChange(item: PaymentMethodItem) {
        if (this.configurationFields(item).length) {
            item.method.configuration ??= {};
        }
    }

    protected typeName(item: PaymentMethodItem) {
        return this.types.find(type => type.id === item.method.payment_method_type_id)?.name ?? '';
    }

    protected typeIcon(item: PaymentMethodItem) {
        switch (item.method.payment_method_type_id) {
            case 1: case 19: case 21: case 32: case 38: case 40:
                return 'local_atm';
            case 2: case 6:
                return 'confirmation_number';
            case 8:
                return 'account_balance';
            case 3:
                return 'picture_in_picture';
            case 9: case 10:
                return 'card_giftcard';
            default:
                return 'credit_card';
        }
    }

    protected documentName(document: AvailableDocument) {
        return this.documentPrintersManagerDialogStateService.getDocumentTypeName(document, 'CASHREGISTER.DOCUMENTS_MANAGER');
    }

    protected isSamePrinter(printerA: DocumentPrinter | null, printerB: DocumentPrinter | null) {
        return printerA?.id === printerB?.id;
    }

    protected isSameDocument(documentA: AvailableDocument | null, documentB: AvailableDocument | null) {
        return documentA?.document_template?.id === documentB?.document_template?.id && documentA?.document_type?.id === documentB?.document_type?.id;
    }

    protected onPrinterChange(item: PaymentMethodItem) {
        item.overrideDocument = null;
    }

    protected async toggle(item: PaymentMethodItem, form: NgForm) {
        if (!item.expanded) {
            this.expand(item);
        } else if (form.dirty) {
            if (await this.confirmDialogService.openDialog({ data: { messageLabel: 'SETTINGS.SETTINGS.WANT_TO_CONTINUE_PAYMENT' } })) {
                await this.discardChanges(item, form);
            }
        } else {
            item.expanded = false;
        }
    }

    protected addPaymentMethod() {
        const newItem = this.items().find(item => item.isNew);

        if (newItem) {
            newItem.expanded = true;
            return;
        }

        const usedIds = this.items().map(item => item.method.id);
        const id = Array.from({ length: Math.max(0, ...usedIds) + 1 }, (_, index) => index + 1).find(candidate => !usedIds.includes(candidate))!;

        this.items.update(items => [...items, {
            method: { id, unclaimed: false, hidden: false, color: undefined, print_receipt: true, require_signature: false, configuration: {} } as PaymentMethods,
            isNew: true,
            expanded: true,
            overridePrinter: null,
            overrideDocument: null
        }]);
    }

    protected async save(item: PaymentMethodItem, form: NgForm) {
        const limit = parseInt(String(this.configurationManagerService.getSetting('usage_limits.max_cashdrawers')));

        if (limit && isCashdrawerMethod(item.method.payment_method_type_id)) {
            const cashDrawersCount = this.items().filter(({ method }) => isCashdrawerMethod(method.payment_method_type_id)).length;

            if (cashDrawersCount > limit) {
                this.alert('DEVICE_REGISTRATION.DEVICE_LIMIT_REACHED');
                return;
            }
        }

        const paymentMethod = {
            ...structuredClone(item.method),
            printer_id: item.overridePrinter?.id || null,
            document_type_id: item.overrideDocument?.document_template?.id || item.overrideDocument?.document_type?.id || null
        } as unknown as PaymentMethods;

        try {
            const savedMethod = await (item.isNew
                ? this.entityManagerService.paymentMethods.postOneOnline(paymentMethod)
                : this.entityManagerService.paymentMethods.putOneOnline(paymentMethod));

            item.method = structuredClone(savedMethod);
            item.isNew = false;
            item.expanded = false;

            // The form outlives the fields it contains: it must not be dirty the next time the payment method is opened
            form.form.markAsPristine();

            await this.settingsState.reloadPaymentMethods();
            this.alert('SETTINGS.SETTINGS.EDIT_OF_METHOD_SUCCESSFULL');
        } catch (error) {
            this.alert('SETTINGS.SETTINGS.SAVE_EDIT_ERROR');
        }
    }

    protected async resetConfiguration(item: PaymentMethodItem, form: NgForm) {
        item.method.configuration = null;

        await this.save(item, form);
    }

    protected async remove(item: PaymentMethodItem) {
        if (item.isNew) {
            this.items.update(items => items.filter(({ isNew }) => !isNew));
            return;
        }

        try {
            await this.entityManagerService.paymentMethods.deleteOneOnline(item.method.id);
            this.items.update(items => items.filter(({ method }) => method.id !== item.method.id));

            await this.settingsState.reloadPaymentMethods();
        } catch (error) {
            this.alert('SETTINGS.SETTINGS.SAVE_EDIT_ERROR');
        }
    }

    protected async addPaymentCondition() {
        const paymentCondition = await this.addEditPaymentConditionDialogService.openDialog();

        if (paymentCondition && await this.restManagerService.post('payment_conditions', paymentCondition)) {
            await this.loadPaymentConditions();
        }
    }

    protected async editPaymentCondition(item: PaymentMethodItem, conditionId: number) {
        const paymentConditions = await this.restManagerService.getList('payment_conditions');
        const paymentCondition = paymentConditions.find((condition: { id: number }) => condition.id === conditionId);
        const result = await this.addEditPaymentConditionDialogService.openDialog({ data: { paymentCondition } });

        if (!result) {
            return;
        }

        try {
            if (result.deletePaymentCondition) {
                if (item.method.payment_condition_id === conditionId) {
                    item.method.payment_condition_id = undefined;
                }

                await this.restManagerService.deleteOne(`payment_conditions/${conditionId}`);
            } else {
                await this.restManagerService.put(`payment_conditions/${conditionId}`, null, result);
            }
        } finally {
            await this.loadPaymentConditions();
        }
    }

    private expand(item: PaymentMethodItem) {
        item.expanded = true;
        this.onTypeChange(item);
        item.overridePrinter = this.documentPrinters.find(printer => printer.id == item.method.printer_id) ?? null;

        item.overrideDocument = item.overridePrinter && item.method.document_type_id
            ? item.overridePrinter.availableDocuments?.find(document =>
                document.document_template?.id === Number(item.method.document_type_id) || document.document_type?.id === item.method.document_type_id) ?? null
            : null;
    }

    /** Closes the payment method, throwing away its changes: it is shown as saved the next time it is opened. A new one that was never saved is dropped. */
    private async discardChanges(item: PaymentMethodItem, form: NgForm) {
        const savedMethod = await this.entityManagerService.paymentMethods.fetchOneOffline(item.method.id);

        if (savedMethod) {
            item.method = structuredClone(savedMethod);
            item.expanded = false;
        } else if (item.isNew) {
            this.items.update(items => items.filter(other => other !== item));
        }

        form.form.markAsPristine();
    }

    private async loadPaymentMethods() {
        const paymentMethods = await this.entityManagerService.paymentMethods.fetchCollectionOffline();

        this.items.set(paymentMethods
            .sort((methodA, methodB) => methodA.id - methodB.id)
            .map(method => ({
                method: structuredClone(method),
                isNew: false,
                expanded: false,
                overridePrinter: null,
                overrideDocument: null
            })));
    }

    private async loadPaymentConditions() {
        try {
            const paymentConditions: { id: number; name: string }[] = await this.restManagerService.getList('payment_conditions');

            this.paymentConditions.set([
                { value: null, name: 'SETTINGS.SETTINGS.PAYMENT_CONDITION_NONE' },
                ...paymentConditions.map(({ id, name }) => ({ value: id, name }))
            ]);
        } catch (error) {
            console.error('Error getList payment_conditions: ', error);
        }
    }

    private alert(messageLabel: string) {
        this.alertDialogService.openDialog({ data: { messageLabel: this.translateService.instant(messageLabel) } });
    }
}
